import type { PurchaseOrderListRow } from "@/api/purchase";

export interface KapitalBestellung {
  id: number;
  order_number: string;
  supplier_name: string | null;
  quelle: "rechnung" | "proforma" | "ek" | null;
  wert_usd: number | null;
  wert_eur: number | null;
  bezahlt_usd: number;
  bezahlt_eur: number;
  /** Zahlung in Quellwährung inklusive umrechenbarer Gegenwährung; null ohne Quelle/Kurs. */
  bezahlt_quelle: number | null;
  bezahlt_quelle_umgerechnet: boolean;
  /** Bezahlter Anteil in der Währung der Quelle, begrenzt auf 0..1. */
  bezahlt_anteil: number | null;
  bezahlt_eur_umgerechnet: boolean;
  offen_usd: number | null;
  offen_eur: number | null;
  kurs: number | null;
  geschaetzt: boolean;
  unvollstaendig: boolean;
  /** Proforma-/EK-Wert deckt nicht alle Positionen ab (oder Zähler fehlt). */
  wert_unvollstaendig: boolean;
  eur_unvollstaendig: boolean;
  /** In der Währung der Quelle: USD bei Rechnung/Proforma, sonst EUR. */
  ueberzahlt: number | null;
  positions_ohne_preis: number;
  positions_ohne_ek: number;
}

export interface KapitalSummen {
  bezahlt_eur: number;
  bezahlt_eur_umgerechnet: boolean;
  offen_eur: number;
  gebunden_eur: number;
  offen_mit_preis_eur: number;
  offen_geschaetzt_eur: number;
  unvollstaendig: boolean;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/** Gleiche Auswahl und Rundung wie effektiverKurs im Backend. Die Kurssummen
 * enthalten ausschließlich beidseitig positive Warenzahlungen, nicht alle Zahlungen. */
function zahlungsKurs(rows: PurchaseOrderListRow[]): number | null {
  let usd = 0;
  let eur = 0;
  for (const row of rows) {
    const u = row.kurs_ware_usd ?? 0;
    const e = row.kurs_ware_eur ?? 0;
    if (Number.isFinite(u) && Number.isFinite(e) && u > 0 && e > 0) {
      usd += u;
      eur += e;
    }
  }
  return usd > 0 && eur > 0 ? Math.round(eur / usd * 10000) / 10000 : null;
}

export function berechneKapital(rows: PurchaseOrderListRow[]): {
  bestellungen: KapitalBestellung[];
  summen: KapitalSummen;
} {
  const offene = rows.filter((row) => row.status === "bestellt" || row.status === "in_abwicklung");
  const fallbackKurs = zahlungsKurs(offene);
  const bestellungen = offene.map((row): KapitalBestellung => {
    const quelle = row.invoice_total_usd != null ? "rechnung"
      : row.positions_total_usd != null ? "proforma"
        : row.ek_schaetzung_eur != null ? "ek" : null;
    const kurs = zahlungsKurs([row]) ?? fallbackKurs;
    const wert_usd = quelle === "rechnung" ? row.invoice_total_usd
      : quelle === "proforma" ? row.positions_total_usd! : null;
    const wert_eur = quelle === "ek" ? row.ek_schaetzung_eur!
      : wert_usd !== null && kurs !== null ? round2(wert_usd * kurs) : null;
    const bezahlt_usd = row.paid_ware_usd ?? 0;
    const hatKurs = kurs !== null && kurs > 0;
    const zahlungOhneEur = (row.zahlungen_ohne_eur ?? 0) > 0;
    const zahlungOhneUsd = (row.zahlungen_ohne_usd ?? 0) > 0;
    const bezahlt_eur_umgerechnet = zahlungOhneEur && hatKurs;
    // Nur die fehlende Währungsseite ergänzen; erfasste Beträge bleiben erhalten.
    const bezahlt_eur = round2((row.paid_ware_eur ?? 0)
      + (bezahlt_eur_umgerechnet ? (row.paid_ware_nur_usd ?? 0) * kurs : 0));
    const bezahltUsdMitUmrechnung = bezahlt_usd + (hatKurs ? (row.paid_ware_nur_eur ?? 0) / kurs : 0);
    const offen_usd = wert_usd !== null ? round2(Math.max(0, wert_usd - bezahltUsdMitUmrechnung)) : null;
    // Eine echte Null braucht keinen Wechselkurs; ein unbekannter Rest bleibt null.
    const offen_eur = quelle === "ek" ? round2(Math.max(0, wert_eur! - bezahlt_eur))
      : offen_usd === 0 ? 0 : offen_usd !== null && kurs !== null ? round2(offen_usd * kurs) : null;
    const positions_ohne_preis = row.positions_ohne_preis ?? 0;
    const positions_ohne_ek = row.positions_ohne_ek ?? 0;
    const wert_unvollstaendig = (quelle === "proforma" && (row.positions_ohne_preis == null || positions_ohne_preis > 0))
      || (quelle === "ek" && (row.positions_ohne_ek == null || positions_ohne_ek > 0));
    const unvollstaendig = quelle === null
      || (wert_usd !== null && zahlungOhneUsd && !hatKurs)
      || wert_unvollstaendig;
    const wert = quelle === "ek" ? wert_eur : wert_usd;
    const quelleOhneKurs = !hatKurs && (quelle === "ek" ? zahlungOhneEur : zahlungOhneUsd);
    const bezahlt_quelle = quelle === null || quelleOhneKurs ? null
      : quelle === "ek" ? bezahlt_eur : round2(bezahltUsdMitUmrechnung);
    const bezahlt_quelle_umgerechnet = bezahlt_quelle !== null
      && (quelle === "ek" ? bezahlt_eur_umgerechnet : zahlungOhneUsd && hatKurs);
    const bezahlt_anteil = wert !== null && wert > 0 && bezahlt_quelle !== null
      ? Math.min(1, Math.max(0, bezahlt_quelle / wert)) : null;
    return {
      id: row.id, order_number: row.order_number, supplier_name: row.supplier_name,
      quelle, wert_usd, wert_eur, bezahlt_usd, bezahlt_eur, offen_usd, offen_eur, kurs,
      bezahlt_eur_umgerechnet, bezahlt_quelle, bezahlt_quelle_umgerechnet, bezahlt_anteil,
      geschaetzt: quelle === "ek", unvollstaendig, wert_unvollstaendig,
      eur_unvollstaendig: unvollstaendig || offen_eur === null || row.paid_ware_eur == null
        || (zahlungOhneEur && !hatKurs),
      ueberzahlt: wert_usd !== null ? round2(Math.max(0, bezahltUsdMitUmrechnung - wert_usd))
        : quelle === "ek" ? round2(Math.max(0, bezahlt_eur - wert_eur!)) : null,
      positions_ohne_preis, positions_ohne_ek,
    };
  });
  const summe = (wert: (row: KapitalBestellung) => number) => round2(bestellungen.reduce((sum, row) => sum + wert(row), 0));
  const bezahlt_eur = summe((row) => row.bezahlt_eur);
  const offen_eur = summe((row) => row.offen_eur ?? 0);
  return {
    bestellungen,
    summen: {
      bezahlt_eur, offen_eur, gebunden_eur: round2(bezahlt_eur + offen_eur),
      bezahlt_eur_umgerechnet: bestellungen.some((row) => row.bezahlt_eur_umgerechnet),
      offen_mit_preis_eur: summe((row) => row.geschaetzt ? 0 : row.offen_eur ?? 0),
      offen_geschaetzt_eur: summe((row) => row.geschaetzt ? row.offen_eur ?? 0 : 0),
      unvollstaendig: bestellungen.some((row) => row.eur_unvollstaendig),
    },
  };
}
